import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Badge, Avatar, Dropdown, Drawer } from 'antd';
import { 
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  DownOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  TeamOutlined,
  FileSearchOutlined,
  ShoppingCartOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import './Navbar.css';
import Logo from './Logo';
import { useUser } from '../contexts/UserContext';

const { Header } = Layout;

const Navbar = ({ onLogout, onCarrinhoOpen, carrinhoNotification, carrinhoQuantidade }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAdmin, canManageProducts } = useUser();

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname.startsWith('/admin') || location.pathname === '/users' || location.pathname === '/logs' || location.pathname === '/controle-acessos';
    }
    return location.pathname === path;
  };

  const handleMenuClick = (path) => {
    navigate(path);  // ← Mudou de window.location.href para navigate
    setMobileMenuOpen(false);
  };

  // Menu items para Admin Dropdown
  const adminMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardOutlined />,
      onClick: () => handleMenuClick('/admin')
    },
    {
      key: 'produtos',
      label: 'Produtos',
      icon: <AppstoreOutlined />,
      onClick: () => handleMenuClick('/admin-produtos')
    },
    {
      key: 'pedidos',
      label: 'Pedidos',
      icon: <FileTextOutlined />,
      onClick: () => handleMenuClick('/admin-pedidos')
    },
    {
      key: 'controle-loja',
      label: 'Controle da Loja',
      icon: <ShoppingCartOutlined />,
      onClick: () => handleMenuClick('/controle-loja')
    },
    {
      key: 'usuarios',
      label: 'Usuários',
      icon: <TeamOutlined />,
      onClick: () => handleMenuClick('/users')
    },
    {
      key: 'controle-acessos',
      label: 'Controle de Acessos',
      icon: <BarChartOutlined />,
      onClick: () => handleMenuClick('/controle-acessos')
    },
    {
      key: 'logs',
      label: 'Logs',
      icon: <FileSearchOutlined />,
      onClick: () => handleMenuClick('/logs')
    }
  ];

  // Menu items para Profile
  const profileMenuItems = [
    {
      key: 'email',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontSize: '12px', color: '#999' }}>Olá,</div>
          <div style={{ fontWeight: 600, color: '#024638', marginTop: '4px' }}>{user?.email}</div>
        </div>
      ),
      disabled: true
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogoutOutlined />,
      onClick: () => onLogout(),
      danger: true
    }
  ];

  return (
    <>
      <Header className="navbar-header">
        <div className="navbar-wrapper">
          {/* Logo - Esquerda */}
          <div className="navbar-logo-container" onClick={() => handleMenuClick('/home')}>
            <Logo size="medium" />
          </div>

          {/* Menu Central - Desktop */}
          <div className="navbar-menu-center">
            <div 
              className={`navbar-item ${isActive('/painel') ? 'navbar-item-active' : ''}`}
              onClick={() => handleMenuClick('/painel')}
            >
              PAINEL
            </div>
            
            <div 
              className={`navbar-item ${isActive('/home') ? 'navbar-item-active' : ''}`}
              onClick={() => handleMenuClick('/home')}
            >
              HOME
            </div>
            
            <div 
              className={`navbar-item ${isActive('/meus-pedidos') ? 'navbar-item-active' : ''}`}
              onClick={() => handleMenuClick('/meus-pedidos')}
            >
              <span className="navbar-desktop-text">MEUS PEDIDOS</span>
              <span className="navbar-mobile-text">PEDIDOS</span>
            </div>
            
            {(isAdmin() || canManageProducts()) && (
              <Dropdown 
                menu={{ items: adminMenuItems }} 
                trigger={['click']}
                placement="bottomCenter"
              >
                <div className={`navbar-item navbar-item-dropdown ${isActive('/admin') ? 'navbar-item-active' : ''}`}>
                  ADMIN <DownOutlined style={{ fontSize: '10px', marginLeft: '4px' }} />
                </div>
              </Dropdown>
            )}
            
            <div 
              className="navbar-item navbar-item-carrinho"
              onClick={() => onCarrinhoOpen()}
            >
              <Badge 
                count={carrinhoNotification ? carrinhoQuantidade : 0} 
                showZero={false}
              >
                <ShoppingCartOutlined style={{ fontSize: '20px' }} />
              </Badge>
              <span style={{ marginLeft: '8px' }}>CARRINHO</span>
            </div>
          </div>

          {/* Avatar - Direita */}
          <div className="navbar-avatar-container">
            <Dropdown 
              menu={{ items: profileMenuItems }} 
              trigger={['click']}
              placement="bottomRight"
            >
              <Avatar 
                size={50}
                icon={<UserOutlined />}
                className="navbar-avatar"
              />
            </Dropdown>
          </div>

          {/* Botão Mobile */}
          <div className="navbar-mobile-toggle">
            <MenuOutlined onClick={() => setMobileMenuOpen(true)} />
          </div>
        </div>
      </Header>

      {/* Drawer Mobile */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        className="navbar-mobile-drawer"
      >
        <div className="navbar-mobile-menu">
          <div className="navbar-mobile-item" onClick={() => handleMenuClick('/painel')}>
            PAINEL
          </div>
          <div className="navbar-mobile-item" onClick={() => handleMenuClick('/home')}>
            HOME
          </div>
          <div className="navbar-mobile-item" onClick={() => handleMenuClick('/meus-pedidos')}>
            PEDIDOS
          </div>
          {(isAdmin() || canManageProducts()) && (
            <>
              <div className="navbar-mobile-section-title">ADMIN</div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/admin')}>
                <DashboardOutlined /> Dashboard
              </div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/admin-produtos')}>
                <AppstoreOutlined /> Produtos
              </div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/admin-pedidos')}>
                <FileTextOutlined /> Pedidos
              </div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/controle-loja')}>
                <ShoppingCartOutlined /> Controle da Loja
              </div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/users')}>
                <TeamOutlined /> Usuários
              </div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/controle-acessos')}>
                <BarChartOutlined /> Controle de Acessos
              </div>
              <div className="navbar-mobile-item navbar-mobile-subitem" onClick={() => handleMenuClick('/logs')}>
                <FileSearchOutlined /> Logs
              </div>
            </>
          )}
          <div className="navbar-mobile-item" onClick={() => onCarrinhoOpen()}>
            <Badge 
              count={carrinhoNotification ? carrinhoQuantidade : 0}
            >
              <ShoppingCartOutlined style={{ fontSize: '18px' }} />
            </Badge>
            <span style={{ marginLeft: '8px' }}>CARRINHO</span>
          </div>
          <div className="navbar-mobile-divider"></div>
          <div className="navbar-mobile-user">
            <div style={{ fontSize: '12px', color: '#999' }}>Olá,</div>
            <div style={{ fontWeight: 600, marginTop: '4px' }}>{user?.email}</div>
          </div>
          <div className="navbar-mobile-logout" onClick={() => { onLogout(); setMobileMenuOpen(false); }}>
            <LogoutOutlined /> Sair
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default Navbar;
